import { FunctionComponent, useMemo } from "react";
import { ToggleButton, ToggleButtonGroup } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSelector } from "../../../app/helpers";
import { RootState } from "../../../app/rootReducer";
import type { Weekdays } from "../../../components/Pickers/WeekdayPicker/weekday-picker.component";
import { ManagementToolbarField } from "./ManagementToolbarField";
import { MANAGEMENT_TOOLBAR_CONTROL_HEIGHT_PX } from "../utils/management-toolbar.constants";

const BASE_WEEKDAY_KEYS = ["monday", "tuesday", "wednesday", "thursday", "friday"] as const;

const ALL_WEEKDAY_KEYS = [...BASE_WEEKDAY_KEYS, "saturday", "sunday"] as const;

const WEEKDAYS_LABEL_ID = "management-weekdays-field-label";

type ManagementWeekdaysToggleProps = {
  weekdays: Weekdays;
  onChange: (weekdays: Weekdays) => void;
};

/**
 * Compact weekday multi-toggle for Management detail toolbars.
 * Saturday / Sunday only appear when company booking meta allows them.
 */
export const ManagementWeekdaysToggle: FunctionComponent<ManagementWeekdaysToggleProps> = ({
  weekdays,
  onChange
}) => {
  const { t } = useTranslation();
  const bookingsOnSaturdaysAllowed = useSelector(
    (state: RootState) => state.login.userInformation.company?.meta?.bookingsOnSaturdaysAllowed
  );
  const bookingsOnSundaysAllowed = useSelector(
    (state: RootState) => state.login.userInformation.company?.meta?.bookingsOnSundaysAllowed
  );

  const visibleWeekdayKeys = useMemo(() => {
    const days: Array<(typeof ALL_WEEKDAY_KEYS)[number]> = [...BASE_WEEKDAY_KEYS];
    if (bookingsOnSaturdaysAllowed) {
      days.push("saturday");
    }
    if (bookingsOnSundaysAllowed) {
      days.push("sunday");
    }
    return days;
  }, [bookingsOnSaturdaysAllowed, bookingsOnSundaysAllowed]);

  const visibleWeekdaySet = useMemo(() => new Set(visibleWeekdayKeys), [visibleWeekdayKeys]);

  const selected = useMemo(
    () => visibleWeekdayKeys.filter(day => weekdays[day]),
    [visibleWeekdayKeys, weekdays]
  );

  return (
    <ManagementToolbarField label={t("_management_weekdaysFilter")} labelId={WEEKDAYS_LABEL_ID}>
      <ToggleButtonGroup
        size="small"
        value={selected}
        onChange={(_, next: string[]) => {
          const nextSelected = new Set(next);
          const updated = ALL_WEEKDAY_KEYS.reduce((acc, day) => {
            acc[day] = visibleWeekdaySet.has(day) && nextSelected.has(day);
            return acc;
          }, {} as Weekdays);
          onChange(updated);
        }}
        aria-labelledby={WEEKDAYS_LABEL_ID}
        data-testid="management-weekdays-toggle"
        sx={{
          flexWrap: "wrap",
          width: "100%",
          "& .MuiToggleButton-root": {
            flex: 1,
            minWidth: 0,
            height: MANAGEMENT_TOOLBAR_CONTROL_HEIGHT_PX,
            px: 0.5
          }
        }}
      >
        {visibleWeekdayKeys.map(day => (
          <ToggleButton
            key={day}
            value={day}
            aria-label={t(day)}
            data-testid={`management-weekday-${day}`}
          >
            {t(day).slice(0, 2).toUpperCase()}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </ManagementToolbarField>
  );
};
